/**
 * DiagnosticFlowAdmin
 * 
 * Admin page for managing diagnostic complaints, flows, and steps.
 * Catalyst tier only - allows setting up diagnostic guidance pathways.
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Plus, 
  Compass, 
  ChevronRight, 
  Edit, 
  Trash2, 
  BookOpen,
  Loader2,
  AlertCircle,
  GripVertical,
} from 'lucide-react';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  useDiagnosticComplaints,
  useDiagnosticFlows,
  useDiagnosticSteps,
  useStepTraining,
  useCreateComplaint,
  useUpdateComplaint,
  useCreateFlow,
  useCreateStep,
  useUpdateStep,
  useDeleteStep,
  useAddStepTraining,
  useRemoveStepTraining,
} from '@/hooks/useDiagnosticGuidance';
import type { 
  DiagnosticComplaint, 
  DiagnosticFlow, 
  DiagnosticStep,
  DiagnosticFlowWithComplaint,
  TrainingRole,
  TRAINING_ROLE_LABELS,
} from '@/types/diagnosticGuidance';
import { toast } from 'sonner';
import { useIsInternalAdmin } from '@/hooks/useIsInternalAdmin';

export default function DiagnosticFlowAdmin() {
  const isEnabled = useFeatureFlag('diagnosticGuidanceEnabled');
  const { data: isInternalAdmin, isLoading: checkingAdmin } = useIsInternalAdmin();
  
  if (!isEnabled) {
    return (
      <div className="container max-w-6xl py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <Compass className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Diagnostic Guidance</h2>
            <p className="text-muted-foreground">
              Diagnostic Guidance is a Catalyst feature. Upgrade to access step-by-step diagnostic workflows.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (checkingAdmin) {
    return (
      <div className="container max-w-6xl py-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (!isInternalAdmin) {
    return (
      <div className="container max-w-6xl py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-semibold mb-2">Not Authorized</h2>
            <p className="text-muted-foreground">
              Diagnostic flow administration is restricted to internal administrators.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return <DiagnosticFlowAdminContent />;
}

function DiagnosticFlowAdminContent() {
  const [selectedComplaint, setSelectedComplaint] = useState<DiagnosticComplaint | null>(null);
  const [selectedFlow, setSelectedFlow] = useState<DiagnosticFlowWithComplaint | null>(null);
  const [showComplaintDialog, setShowComplaintDialog] = useState(false);
  const [showStepDialog, setShowStepDialog] = useState(false);
  const [editingComplaint, setEditingComplaint] = useState<DiagnosticComplaint | null>(null);
  const [editingStep, setEditingStep] = useState<DiagnosticStep | null>(null);
  
  const { data: complaints, isLoading: loadingComplaints } = useDiagnosticComplaints();
  const { data: flows, isLoading: loadingFlows } = useDiagnosticFlows();
  const { data: steps, isLoading: loadingSteps } = useDiagnosticSteps(selectedFlow?.id);
  
  const createComplaint = useCreateComplaint();
  const updateComplaint = useUpdateComplaint();
  const createFlow = useCreateFlow();
  const createStep = useCreateStep();
  const updateStep = useUpdateStep();
  const deleteStep = useDeleteStep();
  
  const handleSelectComplaint = (complaint: DiagnosticComplaint) => {
    setSelectedComplaint(complaint);
    const flow = flows?.find(f => f.complaint_id === complaint.id && f.is_canonical);
    setSelectedFlow(flow || null);
  };
  
  const handleCreateFlow = async () => {
    if (!selectedComplaint) return;
    try {
      const newFlow = await createFlow.mutateAsync({ complaint_id: selectedComplaint.id });
      // Refresh and select the new flow
      setSelectedFlow({ ...newFlow, complaint: selectedComplaint } as DiagnosticFlowWithComplaint);
      toast.success('Flow created');
    } catch (error) {
      toast.error('Failed to create flow');
    }
  };
  
  return (
    <div className="container max-w-6xl py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Compass className="h-6 w-6" />
            Diagnostic Flow Admin
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure diagnostic complaints, flows, and step-by-step guidance
          </p>
        </div>
        <Button onClick={() => { setEditingComplaint(null); setShowComplaintDialog(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Add Complaint
        </Button>
      </div>
      
      <div className="grid grid-cols-12 gap-6">
        {/* Complaints List */}
        <div className="col-span-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Complaints</CardTitle>
              <CardDescription>Customer-reported issues</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingComplaints ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : complaints && complaints.length > 0 ? (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {complaints.map((complaint) => (
                      <div
                        key={complaint.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedComplaint?.id === complaint.id 
                            ? 'bg-primary/10 border-primary' 
                            : 'hover:bg-accent'
                        }`}
                        onClick={() => handleSelectComplaint(complaint)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{complaint.label}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingComplaint(complaint);
                              setShowComplaintDialog(true);
                            }}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {complaint.key}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No complaints configured yet.</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3"
                    onClick={() => { setEditingComplaint(null); setShowComplaintDialog(true); }}
                  >
                    Add First Complaint
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Flow & Steps */}
        <div className="col-span-8">
          {selectedComplaint ? (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{selectedComplaint.label}</CardTitle>
                    <CardDescription>{selectedComplaint.description || 'No description'}</CardDescription>
                  </div>
                  {selectedFlow ? (
                    <Badge variant="outline">Flow v{selectedFlow.version}</Badge>
                  ) : (
                    <Button size="sm" onClick={handleCreateFlow} disabled={createFlow.isPending}>
                      {createFlow.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                      Create Flow
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {selectedFlow ? (
                  <StepsEditor 
                    flowId={selectedFlow.id} 
                    steps={steps || []} 
                    isLoading={loadingSteps}
                    onAddStep={() => { setEditingStep(null); setShowStepDialog(true); }}
                    onEditStep={(step) => { setEditingStep(step); setShowStepDialog(true); }}
                  />
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Compass className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No flow configured for this complaint.</p>
                    <p className="text-sm mt-1">Create a flow to start adding diagnostic steps.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <ChevronRight className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a complaint to view and edit its diagnostic flow</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      
      {/* Complaint Dialog */}
      <ComplaintDialog
        open={showComplaintDialog}
        onOpenChange={setShowComplaintDialog}
        complaint={editingComplaint}
        onSave={async (data) => {
          try {
            if (editingComplaint) {
              await updateComplaint.mutateAsync({ ...data, id: editingComplaint.id });
              toast.success('Complaint updated');
            } else {
              await createComplaint.mutateAsync(data);
              toast.success('Complaint created');
            }
            setShowComplaintDialog(false);
          } catch (error) {
            toast.error('Failed to save complaint');
          }
        }}
        isLoading={createComplaint.isPending || updateComplaint.isPending}
      />
      
      {/* Step Dialog */}
      {selectedFlow && (
        <StepDialog
          open={showStepDialog}
          onOpenChange={setShowStepDialog}
          step={editingStep}
          flowId={selectedFlow.id}
          nextOrder={(steps?.length || 0) + 1}
          onSave={async (data) => {
            try {
              if (editingStep) {
                await updateStep.mutateAsync({ ...data, id: editingStep.id, flow_id: selectedFlow.id });
                toast.success('Step updated');
              } else {
                await createStep.mutateAsync({ ...data, flow_id: selectedFlow.id });
                toast.success('Step created');
              }
              setShowStepDialog(false);
            } catch (error) {
              toast.error('Failed to save step');
            }
          }}
          isLoading={createStep.isPending || updateStep.isPending}
        />
      )}
    </div>
  );
}

// Steps Editor Component
function StepsEditor({
  flowId,
  steps,
  isLoading,
  onAddStep,
  onEditStep,
}: {
  flowId: string;
  steps: DiagnosticStep[];
  isLoading: boolean;
  onAddStep: () => void;
  onEditStep: (step: DiagnosticStep) => void;
}) {
  const deleteStep = useDeleteStep();
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Diagnostic Steps</span>
        <Button size="sm" variant="outline" onClick={onAddStep}>
          <Plus className="h-4 w-4 mr-1" />
          Add Step
        </Button>
      </div>
      
      {steps.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
          <p className="text-sm">No steps configured yet.</p>
          <p className="text-xs mt-1">Add steps to guide technicians through the diagnostic process.</p>
        </div>
      ) : (
        <Accordion type="single" collapsible className="space-y-2">
          {steps.map((step, index) => (
            <AccordionItem key={step.id} value={step.id} className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3 text-left">
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                  <Badge variant="outline" className="w-8 justify-center">{index + 1}</Badge>
                  <span className="font-medium">{step.title}</span>
                  {step.is_optional && <Badge variant="secondary" className="text-xs">Optional</Badge>}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-2">
                  {step.description && (
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>Skill Depth: {step.skill_depth}</span>
                  </div>
                  
                  {/* Step Training */}
                  <StepTrainingEditor stepId={step.id} />
                  
                  <Separator />
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => onEditStep(step)}>
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-destructive"
                      onClick={() => {
                        if (confirm('Delete this step?')) {
                          deleteStep.mutate({ id: step.id, flow_id: flowId });
                        }
                      }}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}

// Step Training Editor
function StepTrainingEditor({ stepId }: { stepId: string }) {
  const { data: training, isLoading } = useStepTraining(stepId);
  const addTraining = useAddStepTraining();
  const removeTraining = useRemoveStepTraining();
  const [showAddDialog, setShowAddDialog] = useState(false);
  
  // Fetch available artifacts
  const { data: artifacts } = useQuery({
    queryKey: ['training-artifacts-for-step'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_artifacts')
        .select('id, title, provider_name')
        .eq('is_active', true)
        .order('title')
        .limit(100);
      if (error) throw error;
      return data;
    },
  });
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">Training Resources</span>
        <Button size="sm" variant="ghost" className="h-7" onClick={() => setShowAddDialog(true)}>
          <Plus className="h-3 w-3 mr-1" />
          Add Training
        </Button>
      </div>
      
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : training && training.length > 0 ? (
        <div className="space-y-1">
          {training.map((t) => (
            <div key={t.id} className="flex items-center justify-between p-2 rounded bg-muted/50">
              <div className="flex items-center gap-2">
                <BookOpen className="h-3 w-3 text-primary" />
                <span className="text-sm">{t.artifact?.title}</span>
                <Badge variant="outline" className="text-xs">{t.training_role}</Badge>
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                onClick={() => removeTraining.mutate({ id: t.id, step_id: stepId })}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic">No training linked to this step.</p>
      )}
      
      {/* Add Training Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Training to Step</DialogTitle>
          </DialogHeader>
          <AddTrainingForm
            artifacts={artifacts || []}
            onAdd={async (artifactId, role) => {
              await addTraining.mutateAsync({ step_id: stepId, artifact_id: artifactId, training_role: role });
              setShowAddDialog(false);
            }}
            isLoading={addTraining.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Add Training Form
function AddTrainingForm({
  artifacts,
  onAdd,
  isLoading,
}: {
  artifacts: { id: string; title: string; provider_name: string }[];
  onAdd: (artifactId: string, role: string) => Promise<void>;
  isLoading: boolean;
}) {
  const [selectedArtifact, setSelectedArtifact] = useState('');
  const [role, setRole] = useState<TrainingRole>('assist');
  
  return (
    <div className="space-y-4">
      <div>
        <Label>Training Artifact</Label>
        <Select value={selectedArtifact} onValueChange={setSelectedArtifact}>
          <SelectTrigger>
            <SelectValue placeholder="Select training..." />
          </SelectTrigger>
          <SelectContent>
            {artifacts.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.title} ({a.provider_name})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Training Role</Label>
        <Select value={role} onValueChange={(v) => setRole(v as TrainingRole)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="primer">Primer (before starting)</SelectItem>
            <SelectItem value="assist">Assist (during step)</SelectItem>
            <SelectItem value="deep_dive">Deep Dive (advanced)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <DialogFooter>
        <Button 
          onClick={() => onAdd(selectedArtifact, role)} 
          disabled={!selectedArtifact || isLoading}
        >
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Training'}
        </Button>
      </DialogFooter>
    </div>
  );
}

// Complaint Dialog
function ComplaintDialog({
  open,
  onOpenChange,
  complaint,
  onSave,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  complaint: DiagnosticComplaint | null;
  onSave: (data: { key: string; label: string; description?: string }) => Promise<void>;
  isLoading: boolean;
}) {
  const [key, setKey] = useState(complaint?.key || '');
  const [label, setLabel] = useState(complaint?.label || '');
  const [description, setDescription] = useState(complaint?.description || '');
  
  // Reset form when dialog opens
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setKey(complaint?.key || '');
      setLabel(complaint?.label || '');
      setDescription(complaint?.description || '');
    }
    onOpenChange(isOpen);
  };
  
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{complaint ? 'Edit Complaint' : 'Add Complaint'}</DialogTitle>
          <DialogDescription>
            Define a customer complaint that will trigger a diagnostic flow.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Key (snake_case)</Label>
            <Input 
              value={key} 
              onChange={(e) => setKey(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
              placeholder="engine_misfire"
            />
          </div>
          <div>
            <Label>Label</Label>
            <Input 
              value={label} 
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Engine Misfire"
            />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea 
              value={description} 
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Customer reports engine misfires or runs rough..."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={() => onSave({ key, label, description })} 
            disabled={!key || !label || isLoading}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Step Dialog
function StepDialog({
  open,
  onOpenChange,
  step,
  flowId,
  nextOrder,
  onSave,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: DiagnosticStep | null;
  flowId: string;
  nextOrder: number;
  onSave: (data: { step_order: number; title: string; description?: string; is_optional?: boolean; skill_depth?: string }) => Promise<void>;
  isLoading: boolean;
}) {
  const [stepOrder, setStepOrder] = useState(step?.step_order || nextOrder);
  const [title, setTitle] = useState(step?.title || '');
  const [description, setDescription] = useState(step?.description || '');
  const [isOptional, setIsOptional] = useState(step?.is_optional || false);
  const [skillDepth, setSkillDepth] = useState(step?.skill_depth || 'foundation');
  
  // Reset form when dialog opens
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setStepOrder(step?.step_order || nextOrder);
      setTitle(step?.title || '');
      setDescription(step?.description || '');
      setIsOptional(step?.is_optional || false);
      setSkillDepth(step?.skill_depth || 'foundation');
    }
    onOpenChange(isOpen);
  };
  
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{step ? 'Edit Step' : 'Add Step'}</DialogTitle>
          <DialogDescription>
            Define a diagnostic step in the flow.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Step Order</Label>
              <Input 
                type="number"
                value={stepOrder} 
                onChange={(e) => setStepOrder(parseInt(e.target.value) || 1)}
                min={1}
              />
            </div>
            <div>
              <Label>Skill Depth</Label>
              <Select value={skillDepth} onValueChange={setSkillDepth}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="foundation">Foundation</SelectItem>
                  <SelectItem value="applied">Applied</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                  <SelectItem value="specialist">Specialist</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Title</Label>
            <Input 
              value={title} 
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Check spark plugs"
            />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea 
              value={description} 
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Remove and inspect spark plugs for wear, fouling, or damage..."
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox 
              id="optional" 
              checked={isOptional} 
              onCheckedChange={(checked) => setIsOptional(!!checked)} 
            />
            <Label htmlFor="optional" className="font-normal">This step is optional</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={() => onSave({ step_order: stepOrder, title, description, is_optional: isOptional, skill_depth: skillDepth })} 
            disabled={!title || isLoading}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
